import { useLocation } from 'react-router';
import { queryStatusIgnoreIdle } from 'utils/combineQueryStatus';
import { User, useAuth } from 'contexts/AuthContext';
import {
  OrganisationResponse,
  PractitionerData,
  WalletResponse,
  useGetOrganisation,
  useGetPractitionerById,
  useGetPractitionerWallet,
} from 'api';
import { useGetAllOrganisationsAndGroups } from 'components/hooks/useGetAllOrganisationsAndGroups';
import { GroupOrganisationHashMap } from 'utils/createGroupOrganisationMap';

export const useGetTransferUnits = ():
  | {
      isPending: boolean;
      isError: boolean;
      user?: undefined;
      userId?: undefined;
      practitioner?: undefined;
      practitionerId?: undefined;
      wallet?: undefined;
      organisation?: undefined;
      organisations?: undefined;
      organisationsInGroups?: undefined;
      hasMultiOrgs?: undefined;
      isMyAccount?: undefined;
    }
  | {
      isPending: false;
      isError: false;
      user?: User;
      userId: string;
      practitioner?: PractitionerData;
      practitionerId?: string;
      wallet?: WalletResponse;
      organisation?: OrganisationResponse;
      organisations?: OrganisationResponse[];
      organisationsInGroups?: GroupOrganisationHashMap;
      hasMultiOrgs?: boolean;
      isMyAccount: boolean;
    } => {
  const { user } = useAuth();
  const { state } = useLocation();
  const locationState: any = state;

  const organisationQuery = useGetOrganisation(locationState?.organisationId);
  const { data: organisation } = organisationQuery;
  const practitionerQuery = useGetPractitionerById(
    locationState?.practitionerId,
  );
  const { data: practitioner } = practitionerQuery;
  const allOrganisationsAndGroupsQuery = useGetAllOrganisationsAndGroups();
  const { organisations, organisationsInGroups } =
    allOrganisationsAndGroupsQuery;

  const {
    data: wallet,
    status: walletStatus,
    isLoading: isWalletLoading,
  } = useGetPractitionerWallet(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    user!.id,
  );

  const loadingStatus = queryStatusIgnoreIdle(
    organisationQuery,
    {
      status: allOrganisationsAndGroupsQuery.status,
      isLoading: allOrganisationsAndGroupsQuery.isPending,
    },
    practitionerQuery,
    { status: walletStatus, isLoading: isWalletLoading },
  );
  const isMyAccount = locationState?.practitionerId === undefined;
  const isPending = loadingStatus === 'pending';
  const isError = loadingStatus === 'error';
  const hasMultiOrgs = !!(organisations?.length && organisations?.length > 1);

  return isPending || isError
    ? { isPending, isError }
    : {
        isPending,
        isError,
        user,
        userId: user?.id || '',
        practitioner,
        practitionerId: locationState?.practitionerId,
        wallet,
        organisation,
        organisations,
        organisationsInGroups,
        hasMultiOrgs,
        isMyAccount,
      };
};
